import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Copy, Check } from 'lucide-react';
interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const ZELLE_NUMBER = '818 535-7769';
export function PaymentModal({
  isOpen,
  onClose
}: PaymentModalProps) {
  const [copied, setCopied] = useState(false);
  // Close on Escape and lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);
  const handleCopyZelle = async () => {
    try {
      await navigator.clipboard.writeText(ZELLE_NUMBER);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — silently ignore
    }
  };
  return <AnimatePresence data-id="element-252">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" aria-modal="true" role="dialog" aria-labelledby="payment-modal-title" data-id="element-253">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-254" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative w-full max-w-md bg-[#111118] border border-white/10 rounded-3xl p-8 shadow-2xl" data-id="element-255">
            <button onClick={onClose} aria-label="Close payment options" className="absolute top-5 right-5 text-slate-400 hover:text-white transition-colors" data-id="element-256">
              <X size={22} data-id="element-257" />
            </button>

            <h3 id="payment-modal-title" className="text-2xl font-heading font-bold text-white mb-2" data-id="element-258">
              Make a <span className="text-gradient" data-id="element-259">Payment</span>
            </h3>
            <p className="text-sm text-slate-400 mb-8" data-id="element-260">
              Choose your preferred payment method below.
            </p>

            <div className="space-y-4" data-id="element-261">
              {/* Venmo */}
              <a href="https://account.venmo.com/u/mrross23" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-4 w-full px-5 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium transition-all hover:shadow-[0_0_20px_rgba(59,130,246,0.35)] group" data-id="element-262">
                <span className="flex flex-col" data-id="element-263">
                  <span className="text-base font-bold" data-id="element-264">Pay with Venmo</span>
                  <span className="text-xs text-white/80" data-id="element-265">@mrross23</span>
                </span>
                <ExternalLink size={20} className="group-hover:translate-x-0.5 transition-transform" data-id="element-266" />
              </a>

              {/* Zelle */}
              <div className="w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10" data-id="element-267">
                <div className="flex items-center justify-between gap-4" data-id="element-268">
                  <span className="flex flex-col" data-id="element-269">
                    <span className="text-base font-bold text-white" data-id="element-270">
                      Pay with Zelle
                    </span>
                    <span className="text-sm text-slate-400" data-id="element-271">
                      {ZELLE_NUMBER}
                    </span>
                  </span>
                  <button onClick={handleCopyZelle} aria-label="Copy Zelle number" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white transition-colors" data-id="element-272">
                    {copied ? <>
                        <Check size={16} className="text-green-400" data-id="element-273" /> Copied
                      </> : <>
                        <Copy size={16} data-id="element-274" /> Copy
                      </>}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center mt-8" data-id="element-275">
              Have a question about your payment? Call or text 818-381-9905.
            </p>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}